import React, { useState } from 'react';
import styles from './ShadowsEditor.module.css';

type ShadowField = 'primary' | 'primaryHover' | 'card' | 'cardHover' | 'header';

interface ShadowsEditorProps {
  primary: string;
  primaryHover: string;
  card: string;
  cardHover: string;
  header: string;
  onChange: (field: ShadowField, value: string) => void;
  defaults: Record<ShadowField, string>;
}

interface ShadowPreset {
  name: string;
  value: string;
}

export const SHADOW_PRESETS: ShadowPreset[] = [
  { name: 'None', value: 'none' },
  { name: 'Small', value: '0 1px 3px rgba(0, 0, 0, 0.12)' },
  { name: 'Medium', value: '0 4px 6px rgba(0, 0, 0, 0.1)' },
  { name: 'Large', value: '0 10px 15px rgba(0, 0, 0, 0.1)' },
  { name: 'XLarge', value: '0 20px 25px rgba(0, 0, 0, 0.15)' },
  { name: 'Gold Light', value: '0 4px 12px rgba(212, 175, 55, 0.3)' },
  { name: 'Gold Medium', value: '0 6px 16px rgba(212, 175, 55, 0.4)' },
  { name: 'Blue Light', value: '0 2px 8px rgba(0, 122, 255, 0.15)' },
  { name: 'Blue Medium', value: '0 4px 12px rgba(0, 122, 255, 0.25)' },
];

const parseShadow = (value: string): { offsetX: string; offsetY: string; blur: string; spread: string; color: string; alpha: number } => {
  // Parse box-shadow format: "offsetX offsetY blur spread color"
  // Each value can have optional 'px' suffix
  // Handle formats like:
  // - "0 2px 8px rgba(0,0,0,0.1)" - 4 components
  // - "0 2px 8px 0 rgba(0,0,0,0.1)" - 5 components
  // - "0px 2px 8px rgba(0,0,0,0.1)" - with px on all
  // - "-2px -4px 8px rgba(0,0,0,0.1)" - negative offsets

  // Handle 'none' value - return default values
  if (value === 'none') {
    return { offsetX: '0', offsetY: '0', blur: '0', spread: '0', color: 'rgba(0,0,0,0.1)', alpha: 0.1 };
  }

  // Remove 'px' suffix from all numeric values for easier parsing
  const normalized = value.replace(/(\d+)px/g, '$1').replace(/(-\d+)px/g, '$1');

  // Match: offsetX offsetY blur (spread?) color
  // capture groups: [1]=offsetX, [2]=offsetY, [3]=blur, [4]=spread (optional), [5]=color
  const match = normalized.match(/^(-?[\d.]+)\s+(-?[\d.]+)\s+([\d.]+)(?:\s+([\d.]+))?\s+(.+)$/);
  if (!match) {
    return { offsetX: '0', offsetY: '0', blur: '0', spread: '0', color: 'rgba(0,0,0,0.1)', alpha: 0.1 };
  }

  let color = match[5] || 'rgba(0,0,0,0.1)';

  // Don't convert hex color - preserve it as-is
  // Only extract alpha from rgba colors
  const alphaMatch = color.match(/[\d.]+(?=\))/);
  const alpha = alphaMatch ? parseFloat(alphaMatch[0]) : 1;

  return {
    offsetX: match[1] || '0',
    offsetY: match[2] || '0',
    blur: match[3] || '0',
    spread: match[4] || '0',
    color,
    alpha,
  };
};

const buildShadow = (offsetX: string, offsetY: string, blur: string, spread: string, color: string): string => {
  if (color === 'none') return 'none';
  return `${offsetX}px ${offsetY}px ${blur}px${spread ? ' ' + spread + 'px' : ''} ${color}`;
};

export { parseShadow, buildShadow };
export type { ShadowField, ShadowPreset };

export const ShadowsEditor: React.FC<ShadowsEditorProps> = ({
  primary,
  primaryHover,
  card,
  cardHover,
  header,
  onChange,
  defaults,
}) => {
  const [expandedField, setExpandedField] = useState<ShadowField | null>(null);

  const fields: Array<{ key: ShadowField; label: string; helpText: string }> = [
    { key: 'primary', label: 'Primary Shadow', helpText: 'Primary button and interactive element shadow' },
    { key: 'primaryHover', label: 'Primary Hover Shadow', helpText: 'Shadow on primary element hover' },
    { key: 'card', label: 'Card Shadow', helpText: 'Default card and panel shadow' },
    { key: 'cardHover', label: 'Card Hover Shadow', helpText: 'Elevated card shadow on hover' },
    { key: 'header', label: 'Header Shadow', helpText: 'Header and navigation shadow' },
  ];

  const handlePresetSelect = (field: ShadowField, preset: ShadowPreset) => {
    onChange(field, preset.value);
  };

  const handleCustomChange = (
    field: ShadowField,
    offsetX: string,
    offsetY: string,
    blur: string,
    spread: string,
    color: string
  ) => {
    onChange(field, buildShadow(offsetX, offsetY, blur, spread, color));
  };

  const handleReset = (field: ShadowField) => {
    onChange(field, defaults[field]);
  };

  return (
    <div className={styles.shadowsEditor}>
      {fields.map(({ key, label, helpText }) => {
        const value: string = key === 'primary' ? primary : key === 'primaryHover' ? primaryHover : key === 'card' ? card : key === 'cardHover' ? cardHover : header;
        const isExpanded = expandedField === key;
        const parsed = parseShadow(value);

        return (
          <div key={key} className={styles.formGroup}>
            <div className={styles.fieldHeader}>
              <div className={styles.labelSection}>
                <label>{label}</label>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setExpandedField(isExpanded ? null : key)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              </div>
              <button
                type="button"
                className={styles.resetButton}
                onClick={() => handleReset(key)}
                title="Reset to default"
              >
                ↺ Reset
              </button>
            </div>

            {/* Preset Dropdown */}
            <div className={styles.presets}>
              <label className={styles.presetsLabel}>Presets:</label>
              <div className={styles.presetButtons}>
                {SHADOW_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    className={`${styles.presetButton} ${value === preset.value ? styles.presetButtonActive : ''}`}
                    onClick={() => handlePresetSelect(key, preset)}
                    title={preset.value}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Editor (Expandable) */}
            {isExpanded && (
              <div className={styles.customEditor}>
                <div className={styles.editorGrid}>
                  <div className={styles.editorField}>
                    <label>Offset X (px)</label>
                    <input
                      type="number"
                      value={parsed.offsetX}
                      onChange={(e) => handleCustomChange(key, e.target.value, parsed.offsetY, parsed.blur, parsed.spread, parsed.color)}
                      className={styles.numberInput}
                    />
                  </div>

                  <div className={styles.editorField}>
                    <label>Offset Y (px)</label>
                    <input
                      type="number"
                      value={parsed.offsetY}
                      onChange={(e) => handleCustomChange(key, parsed.offsetX, e.target.value, parsed.blur, parsed.spread, parsed.color)}
                      className={styles.numberInput}
                    />
                  </div>

                  <div className={styles.editorField}>
                    <label>Blur (px)</label>
                    <input
                      type="number"
                      value={parsed.blur}
                      onChange={(e) => handleCustomChange(key, parsed.offsetX, parsed.offsetY, e.target.value, parsed.spread, parsed.color)}
                      className={styles.numberInput}
                    />
                  </div>

                  <div className={styles.editorField}>
                    <label>Spread (px)</label>
                    <input
                      type="number"
                      value={parsed.spread}
                      onChange={(e) => handleCustomChange(key, parsed.offsetX, parsed.offsetY, parsed.blur, e.target.value, parsed.color)}
                      className={styles.numberInput}
                    />
                  </div>

                  <div className={styles.editorField}>
                    <label>Color</label>
                    <input
                      type="color"
                      value={parsed.color.startsWith('#') ? parsed.color : '#000000'}
                      onChange={(e) => handleCustomChange(key, parsed.offsetX, parsed.offsetY, parsed.blur, parsed.spread, e.target.value)}
                      className={styles.colorInput}
                    />
                  </div>

                  <div className={styles.editorField}>
                    <label>Opacity</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={parsed.alpha}
                      onChange={(e) => {
                        const baseColor = parsed.color.replace(/,[\s\d.]+\)/, ', ' + e.target.value + ')');
                        handleCustomChange(key, parsed.offsetX, parsed.offsetY, parsed.blur, parsed.spread, baseColor);
                      }}
                      className={styles.opacitySlider}
                    />
                    <span className={styles.opacityValue}>{Math.round(parsed.alpha * 100)}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Live Preview */}
            <div className={styles.preview}>
              <div
                className={styles.previewBox}
                style={{
                  boxShadow: value,
                  padding: '1rem',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div className={styles.previewContent}>
                  <span className={styles.previewLabel}>Preview</span>
                  <code className={styles.previewCode}>{value}</code>
                </div>
              </div>
            </div>

            <span className={styles.helpText}>{helpText}</span>
          </div>
        );
      })}
    </div>
  );
};
